/**
 * Summary Header and Footer Logic
 * Handles multi-tenant preheader and footer rendering logic for:
 * - Randolph College: Conditional preheader based on family selection + always-on footer
 * - Rhodes College: Default preheader + conditional footer
 * - Loyola Maryland: Conditional preheader based on grant value + conditional work-study footer (options 1 & 2 only)
 * - Lebanon Valley College: Conditional preheader based on need-based grant value
 * - University of Tampa: Footer1 (work-study) + Footer2 (disclaimer)
 * - Valparaiso University: Option 3 → disclaimer only; Options 1 & 2 → loans note + disclaimer
 * - WPI: Options 1 & 2 → Financial Aid link header; Option 3 → no header
 */

import { Attribute, EstimateResponse } from "../types";
import {
  AID_CALCULATION_OPTIONS,
  SUMMARY_ATTRIBUTE_NAMES,
  SUMMARY_FORMULAS,
  SUMMARY_TEXT_MARKERS,
} from "../constants";

export interface PreheaderResult {
  headerNote: string;
}

export interface FooterResult {
  footerTextToRender: string;
  showWorkStudyFooter: boolean;
  workStudyFooterText: string;
}

// Constants
const CONSTANTS = {
  ATTRIBUTE_NAMES: SUMMARY_ATTRIBUTE_NAMES,
  FORMULAS: SUMMARY_FORMULAS,
  FAMILY_OPTIONS: {
    HELP_CALCULATE: AID_CALCULATION_OPTIONS.NEED_BASED,
    HAVE_SAI: AID_CALCULATION_OPTIONS.SAI,
    SKIP_OPTION: AID_CALCULATION_OPTIONS.SKIP_NO_NEED,
  },
  TEXT_MARKERS: SUMMARY_TEXT_MARKERS,
};

// Helper to check if formula matches (case-insensitive)
const matchesFormula = (attr: Attribute, formula: string): boolean =>
  !!attr.enablingFormula?.toLowerCase().includes(formula.toLowerCase());

// Helper to find attribute by criteria
const findAttr = (attrs: any[], predicate: (attr: any) => boolean): any =>
  attrs.find(predicate);

/**
 * Get the selected family information option
 */
export const getFamilySelection = (
  derivedAttributesData: EstimateResponse | null,
): string => {
  if (!derivedAttributesData) return "";

  const familyObject = derivedAttributesData.objects?.find(
    (obj: any) => obj.name === "FamilyInformation",
  );
  const aidCalculationAttribute = familyObject?.attributes?.find(
    (attr: any) =>
      attr.name?.toLowerCase().includes(CONSTANTS.TEXT_MARKERS.SAI_INDICATOR) ||
      attr.name?.toLowerCase().includes("option below based"),
  );
  return aidCalculationAttribute?.value || "";
};

/**
 * Determine the preheader text based on multi-tenant logic
 * Priority: WPI conditional → Randolph dual-condition → Lebanon Valley conditional → Rhodes default → Loyola conditional
 */
export const determinePreheaderText = (
  allDerivedAttributes: Attribute[],
  sortedSummaryAttrs: any[],
  derivedAttributesData: EstimateResponse | null,
  needBasedGrantValue: number,
  tenantDisplayName?: string,
): PreheaderResult => {
  const selectedFamily = getFamilySelection(derivedAttributesData);
  const { ATTRIBUTE_NAMES, FORMULAS, FAMILY_OPTIONS, TEXT_MARKERS } = CONSTANTS;

  // WPI: show header for options 1 & 2, no header for option 3
  const isWPI =
    !!tenantDisplayName && tenantDisplayName.toLowerCase().includes("wpi");

  if (isWPI) {
    const showOptions12 =
      selectedFamily === FAMILY_OPTIONS.HELP_CALCULATE ||
      selectedFamily === FAMILY_OPTIONS.HAVE_SAI;
    return {
      headerNote: showOptions12 ? TEXT_MARKERS.WPI_HEADER_OPTIONS12 : "",
    };
  }

  // Find attributes
  const defaultPreHeader = findAttr(
    sortedSummaryAttrs,
    (attr) => attr.name === ATTRIBUTE_NAMES.NET_PRICE && !attr.enablingFormula,
  );

  const randolphPreHeader = findAttr(
    allDerivedAttributes,
    (attr) =>
      attr.name === ATTRIBUTE_NAMES.NET_PRICE &&
      matchesFormula(attr, FORMULAS.GRANT_POSITIVE) &&
      attr.enablingAttribute
        ?.toLowerCase()
        .includes(TEXT_MARKERS.SAI_INDICATOR),
  );

  const lebanonValleyPreHeader = findAttr(
    allDerivedAttributes,
    (attr) =>
      attr.name === ATTRIBUTE_NAMES.NET_PRICE &&
      attr.enablingFormula === ">0" &&
      attr.description?.includes(TEXT_MARKERS.LEBANON_VALLEY),
  );

  const loyolaGrantZero = findAttr(
    allDerivedAttributes,
    (attr) =>
      matchesFormula(attr, FORMULAS.GRANT_ZERO) &&
      attr.name === ATTRIBUTE_NAMES.PREHEADER_HIDDEN,
  );

  const loyolaGrantPositive = findAttr(
    allDerivedAttributes,
    (attr) =>
      matchesFormula(attr, FORMULAS.GRANT_POSITIVE) &&
      attr.friendlyName?.toLowerCase() !== ATTRIBUTE_NAMES.FOOTER2 &&
      !attr.description?.includes(TEXT_MARKERS.WORK_STUDY),
  );

  // Determine which preheader to show
  if (randolphPreHeader) {
    const showRandolph =
      selectedFamily === FAMILY_OPTIONS.HELP_CALCULATE ||
      selectedFamily === FAMILY_OPTIONS.HAVE_SAI;
    if (showRandolph)
      return { headerNote: randolphPreHeader.description || "" };
    if (selectedFamily === FAMILY_OPTIONS.SKIP_OPTION)
      return { headerNote: "" };
  }

  if (lebanonValleyPreHeader) {
    return {
      headerNote:
        needBasedGrantValue > 0 ? lebanonValleyPreHeader.description || "" : "",
    };
  }

  if (defaultPreHeader?.description) {
    return { headerNote: defaultPreHeader.description };
  }

  const headerNote =
    needBasedGrantValue === 0
      ? loyolaGrantZero?.description || ""
      : loyolaGrantPositive?.description || "";

  return { headerNote };
};

/**
 * Determine the footer text based on multi-tenant logic
 * Priority: MMC conditional → Valparaiso conditional → Randolph always-on → Rhodes conditional → Loyola conditional
 */
export const determineFooterText = (
  allDerivedAttributes: Attribute[],
  sortedSummaryAttrs: any[],
  derivedAttributesData: EstimateResponse | null,
  disclaimerTextFromAttr: string,
  needBasedGrantValue: number,
  tenantDisplayName?: string,
): FooterResult => {
  const selectedFamily = getFamilySelection(derivedAttributesData);
  const { ATTRIBUTE_NAMES, FORMULAS, FAMILY_OPTIONS, TEXT_MARKERS } = CONSTANTS;

  // Valparaiso University: detect by authority displayName
  const isValpo =
    !!tenantDisplayName &&
    tenantDisplayName.toLowerCase().includes("valparaiso");

  // Marymount Manhattan College
  const isMMC =
    !!tenantDisplayName &&
    tenantDisplayName.toLowerCase().includes("marymountmanhattan");

  if (isMMC) {
    const showOptions12 =
      selectedFamily === FAMILY_OPTIONS.HELP_CALCULATE ||
      selectedFamily === FAMILY_OPTIONS.HAVE_SAI;
    return {
      footerTextToRender: showOptions12
        ? TEXT_MARKERS.MMC_FOOTER_OPTIONS12
        : TEXT_MARKERS.MMC_FOOTER_OPTION3,
      showWorkStudyFooter: false,
      workStudyFooterText: "",
    };
  }

  if (isValpo) {
    const showOptions12 =
      selectedFamily === FAMILY_OPTIONS.HELP_CALCULATE ||
      selectedFamily === FAMILY_OPTIONS.HAVE_SAI;

    if (showOptions12) {
      return {
        footerTextToRender: TEXT_MARKERS.VALPO_FOOTER_OPTIONS12,
        showWorkStudyFooter: false,
        workStudyFooterText: "",
      };
    }
    // Option 3 or unset
    return {
      footerTextToRender: TEXT_MARKERS.VALPO_FOOTER_OPTION3,
      showWorkStudyFooter: false,
      workStudyFooterText: "",
    };
  }

  // Find footer attributes
  const randolphFooter = findAttr(
    allDerivedAttributes,
    (attr) =>
      attr.name === "" &&
      attr.description?.includes(TEXT_MARKERS.RANDOLPH_FOOTER),
  );

  const rhodesFooterOption3 = findAttr(
    allDerivedAttributes,
    (attr) => attr.enablingValue === FAMILY_OPTIONS.SKIP_OPTION,
  );

  const rhodesFooterOptions12 = findAttr(
    allDerivedAttributes,
    (attr) =>
      matchesFormula(attr, FORMULAS.GRANT_POSITIVE) &&
      !attr.friendlyName?.toLowerCase().includes(ATTRIBUTE_NAMES.FOOTER2) &&
      !attr.description?.includes(TEXT_MARKERS.WORK_STUDY),
  );

  // Find Footer1 (Work-Study footer) - search in both arrays
  const footer1WorkStudy =
    findAttr(sortedSummaryAttrs, (attr) => attr.friendlyName === "Footer1") ||
    findAttr(allDerivedAttributes, (attr) => attr.friendlyName === "Footer1");

  // Find Footer2 (Main disclaimer footer) - search in both arrays
  const footer2Disclaimer =
    findAttr(sortedSummaryAttrs, (attr) => attr.friendlyName === "Footer2") ||
    findAttr(allDerivedAttributes, (attr) => attr.friendlyName === "Footer2");

  // Find standard Footer attribute (fallback)
  const standardFooter = findAttr(
    sortedSummaryAttrs,
    (attr) => attr.name === "Footer" && !attr.friendlyName,
  );

  // Determine footer text with priority:
  // 1. University of Tampa: Check for Footer1/Footer2 first (by friendlyName)
  // 2. Loyola University: Footer2 (work-study) conditional based on family selection + standard Footer (disclaimer)
  // 3. Randolph always-on footer
  // 4. Rhodes conditional footer
  // 5. Generic fallback
  let footerTextToRender = "";
  let showWorkStudyFooter = false;
  let workStudyFooterText = "";

  // Check for University of Tampa's Footer1/Footer2 FIRST
  if (footer1WorkStudy) {
    // Tampa pattern: Footer1 is work-study, Footer2 is disclaimer
    footerTextToRender =
      footer2Disclaimer?.description ||
      CONSTANTS.TEXT_MARKERS.TAMPA_FOOTER ||
      disclaimerTextFromAttr;

    // Additional Work-Study footer: Footer1 - shown when grant > 0 per API enablingFormula
    if (footer1WorkStudy?.description && needBasedGrantValue > 0) {
      showWorkStudyFooter = true;
      workStudyFooterText = footer1WorkStudy.description;
    }
  } else if (footer2Disclaimer) {
    // Loyola University pattern: Footer2 has work-study text
    // The Footer2 description contains work-study text that should only show for options 1 and 2
    const showForOptions12 =
      selectedFamily === FAMILY_OPTIONS.HELP_CALCULATE ||
      selectedFamily === FAMILY_OPTIONS.HAVE_SAI;

    if (showForOptions12 && footer2Disclaimer?.description) {
      showWorkStudyFooter = true;
      workStudyFooterText = footer2Disclaimer.description;
    }

    // Always show the disclaimer footer (either from standardFooter or fallback)
    footerTextToRender = standardFooter?.description || disclaimerTextFromAttr;
  } else if (randolphFooter?.description) {
    // Randolph College - always show their footer
    footerTextToRender = randolphFooter.description;
  } else if (
    rhodesFooterOption3?.description ||
    rhodesFooterOptions12?.description
  ) {
    // Rhodes College - conditional based on family selection

    footerTextToRender =
      selectedFamily === FAMILY_OPTIONS.SKIP_OPTION
        ? rhodesFooterOption3?.description || ""
        : rhodesFooterOptions12?.description || "";
  } else {
    // Generic fallback case
    footerTextToRender = disclaimerTextFromAttr;
  }

  return {
    footerTextToRender,
    showWorkStudyFooter,
    workStudyFooterText,
  };
};
